import React, { useContext, useEffect, useMemo } from 'react'
import { ShepherdOptionsWithType, ShepherdTour, ShepherdTourContext } from 'react-shepherd'

import { offset } from '@floating-ui/react'

import { Button } from '@driftt/dds'

import { CurrentUserContext } from 'context/currentUserContext'
import { segmentTrack, TrackType } from 'utils/tracking'

import { OnboardingShepherdContainer } from './OnboardingShepherd.styles'

import 'shepherd.js/dist/css/shepherd.css'
import './styles.css'

type BeforeShowPromise = () => Promise<void>
export interface SharedOnboardingShepherdProps {
  completedEventName?: TrackType
  dismissedEventName?: TrackType
  startEventName?: TrackType
  onDismiss?: () => void
}
export interface OnboardingShepherdStepProps {
  cssSelector: string
  title: string
  text: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  primaryButtonText?: string
  primaryButtonAction?: 'next' | 'cancel' | 'back' | 'complete'
  secondaryButtonText?: string
  secondaryButtonAction?: 'next' | 'cancel' | 'back' | 'complete'
  showOn?: () => boolean
  secondaryButtonCustomAction?: () => void
  beforeShowPromise?: BeforeShowPromise
  onShow?: () => void
  onHide?: () => void
  trackEvent?: TrackType
  stepNumber?: number
  totalSteps?: number
}

const generateStep = ({
  id,
  cssSelector,
  title,
  text,
  position = 'right',
  primaryButtonText = 'Next',
  primaryButtonAction = 'next',
  secondaryButtonText,
  secondaryButtonAction,
  secondaryButtonCustomAction,
  beforeShowPromise,
  onShow,
  onHide,
  trackEvent,
  stepNumber,
  totalSteps
}: OnboardingShepherdStepProps & { id: string }) => ({
  id,
  attachTo: { element: cssSelector, on: position },
  buttons: [
    {
      classes: 'primary',
      text: primaryButtonText,
      type: primaryButtonAction
    },
    secondaryButtonText &&
      (secondaryButtonAction || secondaryButtonCustomAction) && {
        classes: 'secondary',
        text: secondaryButtonText,
        [secondaryButtonCustomAction ? 'action' : 'type']: secondaryButtonCustomAction
          ? secondaryButtonCustomAction
          : secondaryButtonAction
      }
  ].filter(Boolean),
  scrollTo: false,
  cancelIcon: {
    enabled: true
  },
  title,
  text: [text],
  floatingUIOptions: {
    middleware: [offset({ mainAxis: 14, crossAxis: 14 })]
  },
  when: {
    show: () => {
      const element = document.querySelector(`[data-shepherd-step-id="${cssSelector}"]`)
      const tourContainer = document.querySelector(`[data-shepherd-step-id="${id}"]`)
      const container = document.createElement('div')
      container.className = 'shepherd-step-number-container'
      container.innerHTML = `${stepNumber} OUT OF ${totalSteps}`

      if (element) {
        element.appendChild(container)
      } else if (tourContainer) {
        tourContainer.appendChild(container)
      }

      if (onShow) {
        onShow()
      }
    },
    hide: () => {
      if (onHide) {
        onHide()
      }
    },
    cancel: () => {
      if (onHide) {
        onHide()
      }
    }
  },
  beforeShowPromise,
  trackEvent
})

const tourOptions = {
  defaultStepOptions: {
    cancelIcon: {
      enabled: true
    }
  },
  useModalOverlay: true,
  canClickTarget: false
}

export interface StartTourProps extends SharedOnboardingShepherdProps {
  text: string
  trackingMetadata: Record<string, string | number | undefined>
  runOnMount?: boolean
}

const StartTour = ({
  text,
  startEventName,
  completedEventName,
  dismissedEventName,
  trackingMetadata,
  runOnMount,
  onDismiss
}: StartTourProps) => {
  // Needed per docs
  const tour = useContext(ShepherdTourContext)

  useEffect(() => {
    // Setup event listeners
    if (tour) {
      tour.once('start', () => {
        if (startEventName) {
          segmentTrack(startEventName, trackingMetadata)
        }
      })
      tour.once('complete', () => {
        if (completedEventName) {
          segmentTrack(completedEventName, trackingMetadata)
        }
      })

      tour.once('cancel', () => {
        if (dismissedEventName) {
          segmentTrack(dismissedEventName, trackingMetadata)
        }

        if (onDismiss) {
          onDismiss()
        }
      })
    }

    // only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour])

  // Start tour on mount if runOnMount is true
  useEffect(() => {
    if (tour && tour.isActive() === false && runOnMount) {
      // Delay start to allow for better UX
      setTimeout(() => {
        if (tour) {
          tour.start()
        }
      }, 2000)
    }
    // IMPORTANT - DO NOT INCLUDE and dependencies here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (runOnMount) {
    return <></>
  }

  const handleClick = () => {
    if (tour) {
      tour.start()

      segmentTrack(
        TrackType.DASHBOARD_ONBOARDING_SHEPHERD_TOUR_STARTED_ON_USER_ACTION,
        trackingMetadata
      )
    }
  }

  return (
    <Button variant="tertiary" onClick={handleClick}>
      {text}
    </Button>
  )
}

export interface OnboardingShepherdProps extends SharedOnboardingShepherdProps {
  id: string
  steps: OnboardingShepherdStepProps[]
  ctaText: string
  runOnMount?: boolean
  hidden?: boolean
}

/**
 * OnboardingShepherd
 *
 * @param id - {number} unique id for the tour
 * @param steps {OnboardingShepherdStepProps[]} - array of steps to be displayed in the tour
 * @param ctaText {string} - text to be displayed on the button that starts the tour
 * @param completedEventName {TrackType} - event name to be sent to segment when the tour is completed
 * @param dismissedEventName {TrackType} - event name to be sent to segment when the tour is dismissed
 * @param startEventName {TrackType} - event name to be sent to segment when the tour is started
 * @param runOnMount {boolean} - whether or not to run the tour on mount
 * @returns {JSX.Element} - the onboarding shepherd component
 *
 * @example
 *    <OnboardingShepherd
 *     id="onboarding-shepherd"
 *      steps={[
 *        {
 *         cssSelector: '.onboarding-shepherd-1',
 *         title: 'Title 1',
 *         text: 'Text 1',
 *         position: 'right',
 *         primaryButtonText: 'Next',
 *         primaryButtonAction: 'next',
 *         beforeShowPromise: () => Promise.resolve(),
 *         onShow: () => {},
 *         onHide: () => {},
 *         trackEvent: TrackType.OnboardingShepherdStep1
 *        },
 *         {
 *         cssSelector: '.onboarding-shepherd-2',
 *         title: 'Title 2',
 *         text: 'Text 2',
 *         position: 'right',
 *         primaryButtonText: 'Next',
 *         primaryButtonAction: 'next',
 *         beforeShowPromise: () => Promise.resolve(),
 *         onShow: () => {},
 *         onHide: () => {},
 *         trackEvent: TrackType.OnboardingShepherdStep2
 *        }
 *     ]}
 *     ctaText="Start Tour"
 *     completedEventName={TrackType.OnboardingShepherdCompleted} // optional
 *     dismissedEventName={TrackType.OnboardingShepherdDismissed} // optional
 *     startEventName={TrackType.OnboardingShepherdStarted} // optional
 *     onDismiss={() => {}} // optional
 *    />
 */
const OnboardingShepherd = ({
  ctaText,
  id,
  steps,
  completedEventName,
  dismissedEventName,
  startEventName,
  runOnMount = false,
  hidden = false,
  onDismiss
}: OnboardingShepherdProps) => {
  const currentUser = useContext(CurrentUserContext)

  const trackingMetadata = useMemo(
    () => ({
      userId: currentUser?.id,
      orgId: currentUser?.orgId
    }),
    [currentUser]
  )
  if (!steps.length || hidden) {
    return null
  }

  const tourSteps = steps.map((step, index) => {
    // inject view tracking in beforeShowPromise

    const beforeShowPromise = (): Promise<void> => {
      const { trackEvent } = step as ShepherdOptionsWithType & {
        trackEvent?: TrackType
      }

      if (trackEvent) {
        segmentTrack(trackEvent, trackingMetadata)
      }

      if (typeof step.beforeShowPromise === 'function') {
        return step.beforeShowPromise
          ? step.beforeShowPromise().then(() => Promise.resolve())
          : Promise.resolve()
      } else {
        return Promise.resolve()
      }
    }

    const stepNumber = index + 1
    const totalSteps = steps.length

    if (steps.length - 1 === index) {
      // ensure that the last step dismisses the tour. Also, if the last step has a primaryButtonText
      // defined, use that as the text for the button
      return generateStep({
        primaryButtonText: 'Done',
        ...step,
        id: step.cssSelector,
        primaryButtonAction: 'complete',
        beforeShowPromise,
        stepNumber,
        totalSteps
      })
    } else if (index === 0 && steps.length > 1) {
      // ensure that the first step primary action can ONLY be to go to the next step, if there is more than one step
      return generateStep({
        primaryButtonText: 'Next',
        ...step,
        id: step.cssSelector,
        primaryButtonAction: 'next',
        beforeShowPromise,
        stepNumber,
        totalSteps
      })
    }
    return generateStep({ ...step, id, beforeShowPromise, stepNumber, totalSteps })
  })

  return (
    <OnboardingShepherdContainer>
      <ShepherdTour
        steps={(tourSteps as unknown) as ShepherdOptionsWithType[]}
        tourOptions={tourOptions}>
        <StartTour
          runOnMount={runOnMount}
          trackingMetadata={trackingMetadata}
          completedEventName={completedEventName}
          dismissedEventName={dismissedEventName}
          startEventName={startEventName}
          text={ctaText}
          onDismiss={onDismiss}
        />
      </ShepherdTour>
    </OnboardingShepherdContainer>
  )
}

export default OnboardingShepherd
